import React, { useState, useEffect, useRef } from 'react';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import Sound from 'react-sound';
import moment from 'moment';
import confetti from '../../assets/animations/top-down-confetti.json';
import giftbox from '../../assets/animations/giftbox-unbox.json';

import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { SvgIcon, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { ReactComponent as CheckIcon } from '../../assets/icons/Check.svg';
import FullScreenDialog, { FullScreenDialogProps } from '../FullScreenDialog';

import * as ProfileService from '../../api/profileService';
import * as StampCampaignService from '../../api/stampCampaignService';

const useStyles = makeStyles((theme) => ({
  cardContainer: {
    overflow: 'hidden',
    marginTop: theme.spacing(15),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
  vip: {
    fontWeight: theme.typography.fontWeightBold,
    color: '#ffae23',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  fillRoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: theme.palette.primary.main,
    width: 60,
    height: 60,
    borderRadius: 60 / 2,
    backgroundColor: theme.palette.primary.main,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  icon: {
    color: theme.palette.primary.main,
  },
  timestamp: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
  },
  box: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  close: {
    marginLeft: 'auto',
  },
  closeIcon: {
    color: theme.palette.common.black,
  },
  moreStampContainer: {
    marginTop: theme.spacing(),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    backgroundColor: '#fafafa',
    borderRadius: 10,
    cursor: 'pointer',
    maxWidth: 300,
  },
  lottieGiftbox: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    margin: 'auto',
    width: 500,
    height: 500,
    maxWidth: '100%',
    maxHeight: '100%',
  },
}));

type Props = Pick<FullScreenDialogProps, 'open' | 'onClose'> & {
  campaignId: string;
  quantity: number;
  isMultipleStamp: boolean;
  stampAdded: string;
  handleAddMoreStamp: () => void;
  isComplete: boolean;
  isMilestone: boolean;
};

export default function AddStampSingleUseSuccessDialog({
  onClose,
  open,
  campaignId,
  quantity,
  isMultipleStamp,
  stampAdded,
  handleAddMoreStamp,
  isComplete,
  isMilestone,
}: Props) {
  const classes = useStyles();
  const [profile, setProfile] = useState<ProfileService.Profile | null>(null);
  const [stampCampaign, setStampCampaign] =
    useState<StampCampaignService.StampCampaign | null>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isSoundPlaying, setIsSoundPlaying] = useState(true);
  const appleRedirectURI = process.env.REACT_APP_APPLE_REDIRECT_URI;
  const [loadCompletedAnimation, setLoadCompletedAnimation] =
    useState(isMilestone);
  const lottieRef = useRef<LottieRefCurrentProps>(null);

  useEffect(() => {
    let init = false;
    const fetchProfileStampCampaign = async () => {
      const resProfile = await ProfileService.get();
      const resCampaign = await StampCampaignService.get(campaignId);
      if (!init) {
        setProfile(resProfile);
        setStampCampaign(resCampaign);
      }
    };

    fetchProfileStampCampaign();
    return () => {
      init = true;
    };
  }, [campaignId]);

  useEffect(() => {
    if (loadCompletedAnimation) {
      setTimeout(() => setLoadCompletedAnimation(false), 1000);
    }
  }, [loadCompletedAnimation]);

  if (!profile) {
    return null;
  }

  if (!stampCampaign) {
    return null;
  }

  return (
    <FullScreenDialog open={open}>
      {loadCompletedAnimation && (
        <Lottie
          animationData={giftbox}
          loop={loadCompletedAnimation}
          autoplay={false}
          className={classes.lottieGiftbox}
          lottieRef={lottieRef}
          onDOMLoaded={() => lottieRef.current?.setSpeed(5)}
        />
      )}
      <>
        {!loadCompletedAnimation && (
          <>
            {isPlaying && (
              <Lottie
                animationData={confetti}
                loop={isPlaying}
                autoplay={false}
                style={{
                  left: 0,
                  right: 0,
                  position: 'absolute',
                }}
              />
            )}
            {isSoundPlaying && (
              <Sound
                url={
                  isMultipleStamp
                    ? `${appleRedirectURI}/sound/double-ding.mp3`
                    : `${appleRedirectURI}/sound/ding.mp3`
                }
                playStatus={isSoundPlaying ? 'PLAYING' : 'STOPPED'}
                onFinishedPlaying={() => {
                  setIsSoundPlaying(false);
                  setIsPlaying(false);
                }}
              />
            )}
            <div className={classes.box}>
              <div className={classes.close}>
                <IconButton
                  onClick={() => {
                    if (onClose) {
                      onClose({}, 'backdropClick');
                    }
                  }}
                >
                  <CloseIcon className={classes.closeIcon} />
                </IconButton>
              </div>
            </div>
            <div className={classes.cardContainer}>
              <Typography variant="h4" className={classes.title} gutterBottom>
                {profile.firstName} {profile.lastName}
              </Typography>
              <div className={classes.fillRoot}>
                <SvgIcon
                  component={CheckIcon}
                  color="primary"
                  className={classes.icon}
                  viewBox={'0 0 30 30'}
                  fontSize="large"
                />
              </div>
              {stampCampaign.type === 'passport' && isComplete ? (
                <>
                  <Typography
                    variant="body1"
                    className={classes.timestamp}
                    gutterBottom
                  >
                    {stampCampaign.notification}
                  </Typography>
                  <Typography
                    variant="body1"
                    className={classes.timestamp}
                    gutterBottom
                  >
                    {moment().format('llll')}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography
                    variant="h4"
                    className={classes.title}
                    gutterBottom
                  >
                    {stampAdded} Stamp{stampAdded === '1' ? '' : 's'} Collected
                  </Typography>
                  <Typography
                    variant="body1"
                    className={classes.timestamp}
                    gutterBottom
                  >
                    {moment().format('llll')}
                  </Typography>
                </>
              )}
            </div>
          </>
        )}
      </>
    </FullScreenDialog>
  );
}
